/**
 * Responsibility: ask for an outlier scan, and say what came back.
 *
 * A submitted phrase, not a live one. A scan reads a catalogue page and
 * two eBay searches per card - it is the most expensive thing this app
 * does - so it happens when asked for and never on a keystroke.
 *
 * The grade filter is the point of the feature rather than a refinement
 * of it: hunting raw flips and hunting PSA 10 flips are different hunts
 * over the same scan, and switching between them costs nothing because
 * the rows are already here. Only grades the scan actually found are
 * offered, so no choice can lead to an empty table.
 */
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { useEffect, useState } from 'react';
import { gradeBucketLabel, sortBuckets } from './gradeBuckets';

type OutlierControlsProps = {
  onScan: (query: string) => void;
  initial: string | null;
  bucket: string;
  onBucket: (next: string) => void;
  /** Grades the rows on screen actually cover. */
  buckets: string[];
  busy: boolean;
  /** Re-check the standing list now rather than waiting for the hour. */
  onSweep: () => void;
  watched: number;
  /** Drop a typed scan and go back to the standing list. */
  onClearScan: () => void;
};

export function OutlierControls({
  onScan,
  initial,
  bucket,
  onBucket,
  buckets,
  busy,
  onSweep,
  watched,
  onClearScan,
}: OutlierControlsProps) {
  const [draft, setDraft] = useState(initial ?? '');

  // Re-seeded when the page hands down a different phrase, so the box
  // always shows what produced the rows below it.
  useEffect(() => {
    if (initial !== null) setDraft(initial);
  }, [initial]);

  const submit = () => {
    const phrase = draft.trim();
    if (phrase.length > 1) onScan(phrase);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        size="small"
        label="Scan for"
        placeholder="charizard base set"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => event.key === 'Enter' && submit()}
        sx={{ width: 220 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      <Button
        size="small"
        variant="contained"
        onClick={submit}
        disabled={busy || draft.trim().length < 2}
        startIcon={<TrendingDownIcon />}
        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {busy ? 'Scanning' : 'Scan'}
      </Button>

      {initial ? (
        <Button size="small" onClick={onClearScan} sx={{ whiteSpace: 'nowrap' }}>
          Back to mine
        </Button>
      ) : (
        <span>
          <Button
            size="small"
            variant="outlined"
            onClick={onSweep}
            disabled={busy}
            startIcon={<RefreshIcon />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {busy ? 'Checking' : `Check my ${watched || ''} cards`.replace('  ', ' ')}
          </Button>
        </span>
      )}

      {buckets.length > 0 ? (
        <TextField
          select
          size="small"
          label="Grade"
          value={bucket}
          onChange={(event) => onBucket(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Every grade</MenuItem>
          {sortBuckets(buckets).map((key) => (
            <MenuItem key={key} value={key}>
              {gradeBucketLabel(key)}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
    </Stack>
  );
}

type OutlierBadgesProps = {
  query: string | null;
  /** When the standing sweep last ran. Null while a scan is showing. */
  sweptAt: string | null;
  watched: number;
  shown: number;
  found: number;
  bucket: string;
  onBucket: (next: string) => void;
};

export function OutlierBadges({
  query,
  sweptAt,
  watched,
  shown,
  found,
  bucket,
  onBucket,
}: OutlierBadgesProps) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
      <Chip
        size="small"
        color="primary"
        icon={<TrendingDownIcon sx={{ fontSize: 15 }} />}
        label="Outliers"
      />

      {query ? (
        <Chip size="small" variant="outlined" label={`"${query}"`} />
      ) : (
        <Chip
          size="small"
          variant="outlined"
          label={sweptAt ? `your ${watched} cards` : 'not swept yet'}
        />
      )}

      <Chip size="small" variant="outlined" label={`${shown}/${found}`} />

      {bucket !== '' ? (
        <Chip
          size="small"
          color="secondary"
          label={gradeBucketLabel(bucket)}
          onDelete={() => onBucket('')}
        />
      ) : null}

      <Chip size="small" variant="outlined" color="warning" label="asking prices, unverified" />

    </Stack>
  );
}
